import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../services/api';
import {
  FiFileText, FiDownload, FiCalendar, FiFilter,
  FiUsers, FiTruck, FiDollarSign, FiCheckCircle
} from 'react-icons/fi';

function AdminReports() {
  const [reportType, setReportType] = useState('users');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [generating, setGenerating] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);

  // Charger l'historique depuis localStorage
  const loadReportsFromStorage = () => {
    try {
      const stored = localStorage.getItem('admin_generated_reports');
      if (stored) {
        const reports = JSON.parse(stored);
        // Filtrer les rapports anciens de plus de 30 jours
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return reports.filter(report => new Date(report.generatedAt) > thirtyDaysAgo);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rapports depuis localStorage:', error);
    }
    return [];
  };

  // Sauvegarder dans localStorage à chaque modification
  const saveReportsToStorage = (reports) => {
    try {
      localStorage.setItem('admin_generated_reports', JSON.stringify(reports));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des rapports dans localStorage:', error);
    }
  };

  // Charger les rapports depuis localStorage au montage du composant
  useEffect(() => {
    const reports = loadReportsFromStorage();
    setGeneratedReports(reports);
  }, []);

  const reportTypes = [
    {
      id: 'users',
      name: 'Rapport des utilisateurs',
      description: 'Liste complète des utilisateurs avec leurs statistiques',
      icon: <FiUsers className="w-6 h-6" />
    },
    {
      id: 'moves',
      name: 'Rapport des déménagements',
      description: 'Tous les déménagements avec détails et statistiques',
      icon: <FiTruck className="w-6 h-6" />
    },
    {
      id: 'payments',
      name: 'Rapport financier',
      description: 'Revenus, paiements et transactions',
      icon: <FiDollarSign className="w-6 h-6" />
    },
    {
      id: 'tickets',
      name: 'Rapport des tickets',
      description: 'Tous les tickets de support avec résolutions',
      icon: <FiCheckCircle className="w-6 h-6" />
    },
    {
      id: 'conversion',
      name: 'Rapport de conversion',
      description: 'Analyse des taux de conversion et performance',
      icon: <FiFileText className="w-6 h-6" />
    }
  ];

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      
      // Générer et télécharger directement le PDF
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reportType,
          startDate: dateRange.start,
          endDate: dateRange.end
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      // Créer un blob et télécharger
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-${reportType}-${dateRange.start}-${dateRange.end}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Ajouter à l'historique
      const report = {
        id: Date.now(),
        type: reportType,
        name: reportTypes.find(r => r.id === reportType)?.name,
        generatedAt: new Date().toISOString(),
        period: { start: dateRange.start, end: dateRange.end }
      };
      
      const updatedReports = [report, ...generatedReports].slice(0, 20); // Garder les 20 derniers
      setGeneratedReports(updatedReports);
      saveReportsToStorage(updatedReports);
      
      alert(`Rapport "${report.name}" généré et téléchargé avec succès !`);
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
      alert('Erreur lors de la génération du rapport: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadReport = async (report) => {
    try {
      // Télécharger le PDF depuis le serveur
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reportType: report.type,
          startDate: report.period.start,
          endDate: report.period.end
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      // Créer un blob et télécharger
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-${report.type}-${report.period.start}-${report.period.end}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Mettre à jour la date de dernière utilisation
      const updatedReports = generatedReports.map(r => 
        r.id === report.id ? { ...r, lastDownloaded: new Date().toISOString() } : r
      );
      setGeneratedReports(updatedReports);
      saveReportsToStorage(updatedReports);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du rapport PDF');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-yellow-400 mb-2">Rapports</h1>
        <p className="text-gray-400">Générer et télécharger des rapports détaillés</p>
      </div>

      {/* Configuration du rapport */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-6">Générer un nouveau rapport</h2>
        
        {/* Type de rapport */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Type de rapport
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setReportType(type.id)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  reportType === type.id
                    ? 'border-yellow-500 bg-yellow-500/10'
                    : 'border-gray-700 bg-gray-900/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className={`mr-3 ${
                    reportType === type.id ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {type.icon}
                  </div>
                  <span className={`font-semibold ${
                    reportType === type.id ? 'text-yellow-400' : 'text-white'
                  }`}>
                    {type.name}
                  </span>
                </div>
                <p className="text-sm text-gray-400">{type.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Période */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date de début
            </label>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date de fin
            </label>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
              />
            </div>
          </div>
        </div>

        {/* Bouton de génération */}
        <button
          onClick={handleGenerateReport}
          disabled={generating}
          className="w-full md:w-auto px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating ? (
            <>
              <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mr-2"></div>
              Génération en cours...
            </>
          ) : (
            <>
              <FiFileText className="w-5 h-5 mr-2" />
              Générer le rapport
            </>
          )}
        </button>
      </div>

      {/* Rapports récents */}
      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Rapports générés</h2>
          {generatedReports.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm('Êtes-vous sûr de vouloir supprimer tout l\'historique des rapports ?')) {
                  setGeneratedReports([]);
                  saveReportsToStorage([]);
                }
              }}
              className="px-3 py-1 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
            >
              Effacer l'historique
            </button>
          )}
        </div>
        {generatedReports.length === 0 ? (
          <div className="text-center py-12">
            <FiFileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Aucun rapport généré</p>
            <p className="text-gray-500 text-sm mt-2">Générez votre premier rapport ci-dessus</p>
          </div>
        ) : (
          <div className="space-y-4">
            {generatedReports.map((report) => (
              <div
                key={report.id}
                className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="p-3 bg-yellow-500/20 rounded-lg">
                    <FiFileText className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">{report.name}</p>
                    <p className="text-gray-400 text-sm">
                      Période: {new Date(report.period.start).toLocaleDateString('fr-FR')} - {new Date(report.period.end).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Généré le {new Date(report.generatedAt).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
              <button
                onClick={() => handleDownloadReport(report)}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors flex items-center ml-4"
              >
                <FiDownload className="w-4 h-4 mr-2" />
                Télécharger PDF
              </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Informations */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-blue-400 text-sm">
          <strong>Note:</strong> Les rapports sont générés au format PDF professionnel et incluent toutes les données de la période sélectionnée. 
          Les rapports peuvent prendre quelques secondes à générer selon la quantité de données. 
          Les PDFs sont optimisés pour l'impression et le partage.
        </p>
      </div>
    </div>
  );
}

export default AdminReports;

